import type { FC } from 'react';

import { Placeholder, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import { Box, Image, PageGrid, Stack, Stretch, Text } from '@eneco/sparky';
import type { BoxProps, TextProps } from '@eneco/sparky/types';

import type { SectionContent } from './section-content.types';
import { resolveSlot, SECTION_SLOTS } from './slots';

/**
 * `Section` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Layout mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/Section/Section.tsx`):
 * a background image/color band behind a single-column content stack, with extra bottom padding when
 * followed by a component that pulls itself up with a negative margin.
 *
 * Known simplification: the legacy component also wraps a `TitleTextCTA` child in an extra
 * `Grid columns="12"` / `span 8` layout by introspecting the *rendered* JSX of its Sitecore
 * `<Placeholder>` output. The CMS-agnostic `<Placeholder>` (`@eneco/cms`) renders opaquely — it does
 * not expose child renderings for post-hoc wrapping — so that per-child grid treatment is dropped here.
 * `backgroundImage` is Sitecore-only; see `section-content.types.ts`.
 *
 * Known simplification: the legacy component overlays its background image *behind* the content via
 * absolutely-positioned wrappers (`libs/custom-components/src/Section/Section.tsx`'s `Relative`/
 * `Background`). `lib:cms-components`'s module boundary (`nx.json`) does not permit depending on
 * `@eneco/custom-components`, and this package's lint rules (`dxp-rules/no-inline-css`) forbid
 * hand-rolled positioning CSS as a substitute — no Sparky-provided primitive offers this exact overlay.
 * `backgroundColor` still applies to the whole band via `Box`; a background image, when present, renders
 * as a normal block above the content instead of behind it.
 */
const Section: FC = () => {
  const content = useContent<SectionContent>();
  const mapImage = useImageMapper();

  const backgroundColor = useSetting('backgroundColor') as BoxProps['backgroundColor'];
  const textColor = useSetting('textColor') as TextProps['color'];
  const anchorId = useSetting('anchorId');
  const isFollowedByNegativeMargin = useSetting('isFollowedByNegativeMargin') === 'true';

  const rendering = useRendering();
  const mainSlot = resolveSlot(rendering, SECTION_SLOTS.main);
  const mainChildren = (mainSlot && rendering?.placeholders?.[mainSlot]) || [];
  const lastChild = mainChildren[mainChildren.length - 1];
  const isLastChildCenteredWithDivider =
    lastChild?.componentName === 'Centered' &&
    (lastChild.params?.hasDivider === 'true' || (lastChild.content as Record<string, unknown> | undefined)?.hasDivider === true);

  const imageProps = mapImage(content.backgroundImage);

  let paddingBottom: BoxProps['paddingBottom'];
  if (isLastChildCenteredWithDivider) {
    paddingBottom = { initial: '0', md: '0' };
  } else if (isFollowedByNegativeMargin) {
    paddingBottom = { initial: '10', md: '16', xl: '32' };
  } else {
    paddingBottom = { initial: '10', md: '16' };
  }

  return (
    <Box
      as="section"
      backgroundColor={backgroundColor}
      paddingY={isFollowedByNegativeMargin ? { initial: '10', md: '16', xl: '6' } : { initial: '10', md: '16' }}
      paddingBottom={paddingBottom}
      id={anchorId}>
      {imageProps?.src ? (
        <Image src={imageProps.src} alt={imageProps.alt ?? ''} width="100%" height="auto" objectFit="cover" sources={imageProps.sources} />
      ) : null}
      <PageGrid>
        <Stretch width>
          <PageGrid.Item gridColumn="1/-1">
            <Text color={textColor} as="div">
              <Stack gap={{ initial: '10', md: '16' }} direction="column">
                {mainSlot ? <Placeholder name={mainSlot} /> : null}
              </Stack>
            </Text>
          </PageGrid.Item>
        </Stretch>
      </PageGrid>
    </Box>
  );
};

export default Section;
